// tslint:disable:max-line-length

import * as React from "react";
// import 'webrtc-adapter';
import TranslateNew from "@translate/TranslateNew";
import { FormControlLabel, Switch, WithStyles, withStyles} from "@material-ui/core";
import {ConnecycubeStyle} from "./connectycube.style";
import PhoneIcon from '@material-ui/icons/CallRounded';
import PhoneEndIcon from '@material-ui/icons/CallEndRounded';
import { ICall, ICallData, ICallParticipant } from "@app-types/websocket/calls.types";
import * as CallActions from '@services/calls/calls.actions';
import * as UserActions from '@services/user/user.actions';
import { connect } from "react-redux";
import { ensureCallPermission, ICallPermissionResult } from "@services/permissions/permission";
import CordovaContext from 'src/cordovaContext';
import CallPermissionDialog from "./CallPermissionDialog";
import {
  countConnectedOthers,
  countPresent,
  getParticipant,
  hasExited,
  isCallEligibleForDisplay,
  isCallJoinable,
  isPresent,
  MAX_CALL_LIFETIME_MS,
  wasCallPlacedOnThisDevice,
} from "@services/calls/calls.utils";
// import ConnectyCube from "connectycube/dist/types/ConnectyCube";

const tn: TranslateNew = TranslateNew.getInstance();
const CREDENTIALS = {appId: 9677, authKey: 'C688FE75-C143-443B-A86C-B3A94B972FED'};
const LOCAL_MEDIA_RETRY_DELAYS_MS = [250, 750, 1500];
const LOCAL_MEDIA_VISIBILITY_WAIT_MS = 2500;
const MEDIA_PERMISSION_PROBE_COOLDOWN_MS = 350;
/**
 * How long after a media permission dialog a native "user left" event is still treated as
 * the dialog rather than the user. The events reach JS asynchronously over the Cordova
 * bridge, so one raised when the dialog opened can arrive after it has closed again.
 */
const MEDIA_PERMISSION_LEAVE_GRACE_MS = 1500;
/**
 * How long a tapped call notification may keep the placeholder screen up while the call
 * itself is still on its way. Kept short on purpose: a notification for a call that ended
 * long ago must not take over the screen.
 */
const CALL_NOTIFICATION_SETUP_WINDOW_MS = 20000;
/**
 * How long a call stays dismissed after hanging up. Only bridges the moment between
 * tearing down locally and the backend confirming the exit; after that the participant's
 * own exit time keeps the call away, and being invited back in still works.
 */
const DISMISSED_CALL_GUARD_MS = 10000;

interface IProps extends WithStyles<typeof ConnecycubeStyle> {
  calls: ICall[];
  callData: ICallData;
  family: any[];
  userId: number;
  isOnline:boolean;
  call: (call:ICall) => void;
  respond: (response:ICallParticipant) => void;
  reportProblem: (msg:string) => void;
  displayErrorMessage: (msg: string) => void;
  callNotificationTime:number;
  callNotificationCallId:number;
  callRequestChatId:number;
  callRequestTime:number;
}

interface IState {
  preparedCall?:ICall,
  session:any,
  videoSession:any,
  callSetupState:'IDLE'|'PREPARING'|'READY',
  isInCall:boolean,
  dismissedCallId?:number,
  dismissedCallAt:number,
  clickedAnswer:number,
  settingUpCall:number,
  video:boolean;
  remoteStreams:string[];
  init:boolean;
  showErrorScreen:boolean;
}

class CallDisplay extends React.Component<IProps, IState> {

  private myVideoRef = React.createRef<HTMLVideoElement>();
  private localStream?: MediaStream; 
  private localMediaPromise?: Promise<boolean>;
  private localMediaReady:boolean = false;
  private callSetupToken:number = 0;
  private activePreparedCallId?:number;
  /** The call the user is taking on this device, as opposed to on another of their devices. */
  private engagedCallId?:number;
  private remoteStreamsByUser: Map<string, MediaStream> = new Map();

  private refrasher?: NodeJS.Timeout;

  private prepCallTime:number = 0;
  private closeCallTime:number = 0;
  private joinCallTime:number = 0;
  private componentMounted:boolean = false;
  private webWakeLock:any;
  private nativeWakeLockActive:boolean = false;
  private nativeWakeLockRequestInFlight:boolean = false;
  private mediaPermissionRequestInProgress:boolean = false;
  private mediaPermissionPromptEndedAt:number = 0;

  constructor(props: IProps) {
    super(props);
    this.pauseHandler = this.pauseHandler.bind(this);

    this.state = {
      session:undefined,
      videoSession:undefined,
      callSetupState:'IDLE',
      preparedCall:undefined,
      isInCall:false,
      dismissedCallId:undefined,
      dismissedCallAt:0,
      clickedAnswer:0,
      settingUpCall:0,
      video:true,
      remoteStreams:[],
      init:false,
      showErrorScreen:false,
    }
  }

  /**
   * Handles a tapped call notification. The push only carries a call id, so the call
   * itself may not have arrived over the websocket yet; until it does, a placeholder is
   * shown. The window is deliberately short - a notification that is tapped long after
   * the call ended must not put a full screen "calling" overlay in front of the user.
   */
  public initCall() {
    if (this.refrasher) {
      clearTimeout(this.refrasher);
      this.refrasher = undefined;
    }

    const {callNotificationCallId, callNotificationTime, calls, userId} = this.props;

    if (callNotificationCallId <= 0 || Date.now() - CALL_NOTIFICATION_SETUP_WINDOW_MS >= callNotificationTime) {
      if (this.state.settingUpCall !== 0) {
        this.setState({settingUpCall:0});
      }
      return;
    }

    const notifiedCall:ICall|undefined = calls.find((c) => c.id !== undefined && c.id === callNotificationCallId);

    if (notifiedCall) {
      // The call is known now, so checkForCalls owns it. Only drop the placeholder, which
      // otherwise keeps covering the screen after the call is no longer joinable.
      if (this.state.settingUpCall !== 0 && !isCallJoinable(notifiedCall, userId)) {
        this.setState({settingUpCall:0});
      }
      return;
    }

    if (this.state.settingUpCall !== callNotificationCallId) {
      this.setState({settingUpCall:callNotificationCallId});
    }
    this.refrasher = setTimeout(() => {
      this.initCall();
    }, 1000);
  }

  public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (prevProps.isOnline !== this.props.isOnline && this.props.isOnline) {
      this.checkForCalls();
    }
    if (prevProps.calls !== this.props.calls) {
      this.checkForCalls();
    }
    if (prevProps.callData !== this.props.callData) {
      this.checkForCalls();
    }
    if (prevProps.callNotificationTime !== this.props.callNotificationTime) { 
      this.initCall();
    }
    if (prevState.preparedCall !== this.state.preparedCall
      || prevState.isInCall !== this.state.isInCall
      || prevState.settingUpCall !== this.state.settingUpCall) {
      this.refreshCallWakeLocks();
    }
  }

  public componentDidMount(): void {
    this.componentMounted = true;

    if (Date.now() - CALL_NOTIFICATION_SETUP_WINDOW_MS < this.props.callNotificationTime && this.props.callNotificationCallId > 0) {
      this.initCall();
    }

    document.addEventListener('pause', this.pauseHandler, false);
    document.addEventListener('vvandroiduserleave', this.androidUserLeaveHandler, false);
    document.addEventListener('resume', this.resumeWakeLockHandler, false);
    document.addEventListener('visibilitychange', this.visibilityWakeLockHandler, false);

    this.setState({ init: true }, () => {
      this.checkForCalls();
    });
  }

  public componentWillUnmount(): void {
    if (this.refrasher) {
      clearTimeout(this.refrasher);
      this.refrasher = undefined;
    }

    this.componentMounted = false;
    document.removeEventListener('pause', this.pauseHandler, false);
    document.removeEventListener('vvandroiduserleave', this.androidUserLeaveHandler, false);
    document.removeEventListener('resume', this.resumeWakeLockHandler, false);
    document.removeEventListener('visibilitychange', this.visibilityWakeLockHandler, false);
    this.releaseCallWakeLocks();
    this.invalidateCallSetup();
    this.releaseConnectyCubeResources();
    this.stopAndReleaseLocalStream();
  }

  public render() {
    const {classes, userId, family} = this.props;

    const currentCall:ICall|undefined = this.getCurrentCall();

    let showAnswerButton = false;
    const waitingFor:number[] = [];
    let name:string = "";
    let myStatus:ICallParticipant|undefined;

    let currentCallParticipants = 0;

    if (currentCall) {
      const others:number[] = [];
      for (const p of currentCall.participants) {
        if (p.participant === userId) { 
          myStatus = p;
        } else {
          others.push(p.participant);
        }
        if ((p.cleanExitTime === undefined || p.cleanExitTime === 0) && (p.responseStatus === 'AWAITING')) {
          waitingFor.push(p.participant);
        }
        if (p.responseStatus === 'ANSWER' && (p.cleanExitTime === undefined || p.cleanExitTime === 0)) {
          currentCallParticipants++;
        }
      }
      name = tn.familyMemberNames(others, family);
      if (currentCall.initiator !== userId && myStatus && myStatus.responseStatus === 'AWAITING') {
        showAnswerButton = true;
      }
      if (Date.now() - 2000 < this.state.clickedAnswer) {
        showAnswerButton = false;
      }
    }

    const incomingCallAwaiting = !!(currentCall && currentCall.initiator !== userId && myStatus && myStatus.responseStatus === 'AWAITING');
    const answerButtonEnabled = incomingCallAwaiting
      && !!currentCall?.externalId
      && this.props.isOnline
      && !!this.props.callData?.connectycubeId
      && !!this.props.callData?.connectycubePassword
      && !!this.state.videoSession
      && this.state.callSetupState === 'READY'
      && !this.state.isInCall;
    const showAnswerInitializing = incomingCallAwaiting && !answerButtonEnabled;
    // Everybody on the call has to be told it is recorded, not just the person picking up.
    // For the caller that window is the whole time it is ringing: they join the moment
    // somebody answers, so the notice has to be up before that.
    const outgoingCallPending = !!(currentCall && currentCall.initiator === userId && !this.state.isInCall);
    const showRecordingNotice = incomingCallAwaiting
      || outgoingCallPending
      || (this.state.settingUpCall > 0 && !currentCall);
    
    const remoteVideos:JSX.Element[] = [];


    for (let i = 0; i < 4; i++) {
      remoteVideos.push(
        <video
          key={""+i}
          poster=""
          playsInline={true}
          id={"videochat"+i}
          controls={false}
          style={this.generateVideoLayout(this.state.remoteStreams.length, i)}
          className={classes.videoChatVideo}
        />);
    }

    return (
      <>
      <CallPermissionDialog
        open={this.state.showErrorScreen}
        onClose={() => this.setState({showErrorScreen:false})}
      />
      <div className={classes.backdrop} style={(currentCall || this.state.settingUpCall > 0)
        ?{}
        :{opacity:0, pointerEvents:'none', maxWidth:'0px', maxHeight:'0px'}} >
        {(this.state.settingUpCall > 0 && !currentCall) && <div className={classes.callInfo}>
          <span>{tn.lang.initializing+"..."}</span>
        </div>}
        {currentCall && <div className={classes.callInfo} style={currentCallParticipants>1?{opacity:0}:{}}>
          <span>{(currentCall && currentCall.initiator === userId && !this.state.isInCall) && tn.lang.calling}</span>
          <span>{(currentCall && this.state.isInCall) && (tn.lang.connecting+"...")}</span>
          <span style={{fontSize:'40px', margin: '20px 0px'}}>{name}</span>
          <span>{(currentCall && currentCall.initiator !== userId && !this.state.isInCall) && tn.lang.calledBy}</span>
        </div>}
        {showRecordingNotice && <div className={classes.callRecordingNotice}>
          {tn.lang.callsRecorded}
        </div>}
        {showAnswerInitializing && <div className={classes.answerButtonStatus}>
          {tn.lang.initializing}
        </div>}
        <div className={classes.videoChat} style={currentCallParticipants>1
          ?{width:'20%', height:'20%', top:'86%', left:'86%', zIndex:1700, border: '1px solid #fff', padding: '4px', 
            borderRadius: '6px',opacity:currentCall?1:0}
          :{zIndex:1700,opacity:currentCall?1:0}}>       
          <video
            poster=""
            playsInline={true}
            autoPlay={true}
            muted={true}
            id='myvideo'
            ref={this.myVideoRef}
            controls={false}
            className={classes.videoChatVideo}
            onClick={() => this.muteVideo()}
          />
          {(currentCallParticipants < 2) && <FormControlLabel style={{paddingLeft:'calc(50% - 19px)'}}
            onClick={(event:any) => this.muteVideo()}
            control={<Switch checked={(this.state.video)} readOnly={true}/>} 
            label={<span style={{color:'#ccc'}}>{this.state.video ? tn.lang.videoOn : tn.lang.videoOff}</span>} />}
        </div>
        <div style={{width:'100%', height:'100%', position:'absolute', opacity:currentCall?1:0, pointerEvents:'none', top:0, left:0}}>
          <div className={classes.videoWall}>
          {remoteVideos}   
          </div>
          <div 
            id="video-overlay-myvideo"
            style={{
              display:this.state.isInCall?'none':'block',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#000',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />


        </div>
        {incomingCallAwaiting && <div
          className={answerButtonEnabled ? classes.answerButton : `${classes.answerButton} ${classes.answerButtonDisabled}`}
          style={answerButtonEnabled ? {} : {opacity: 1, pointerEvents:'none'}}
        >
          <PhoneIcon
            style={{color: answerButtonEnabled ? '#fff' : 'rgba(255, 255, 255, 0.75)', fontSize:40, margin:'auto'}}
            onClick={() => { if (currentCall && answerButtonEnabled) this.joinCall(currentCall) }}
          />
        </div>}
        {currentCall && <div className={classes.denyButton}>
          <PhoneEndIcon style={{color:'#fff', fontSize:40, margin:'auto'}} onClick={() => { if (currentCall) this.hangupCall(currentCall); }} />
        </div>}
        {(this.state.settingUpCall > 0 && !currentCall) && <div className={classes.denyButton}>
          <PhoneEndIcon style={{color:'#fff', fontSize:40, margin:'auto'}} onClick={() => { 
            this.setState({settingUpCall:0, dismissedCallId:this.state.settingUpCall, dismissedCallAt:Date.now()});
            this.props.respond({participant:this.props.userId, responseStatus:'DENIED', callId:this.state.settingUpCall});; }} />
        </div>}
      </div>
      </>
    );
  }

  /**
   * Whether a media permission dialog is up, or was up just now. Native lifecycle events
   * reach JS asynchronously, so one raised by the dialog can still arrive after it closed.
   */
  private isMediaPermissionPromptRecent = () => {
    return this.mediaPermissionRequestInProgress
      || (this.mediaPermissionPromptEndedAt > 0
        && Date.now() - this.mediaPermissionPromptEndedAt < MEDIA_PERMISSION_LEAVE_GRACE_MS);
  }

  private pauseHandler() {
    if (this.shouldKeepScreenAwake()) {
      if (this.isMediaPermissionPromptRecent() || this.isAndroidCordova()) {
        // Permission prompts are part of call setup, not a user hang-up.
        // Android uses the more precise native user-leave/lock event below.
        return;
      }

      this.endCallFromLifecycle('ios_pause');
    } else {
      this.disConnect();
    }
  }

  private androidUserLeaveHandler = (event:any) => {
    if (!this.shouldKeepScreenAwake()) {
      return;
    }

    const reason = event && typeof event.reason === 'string' ? event.reason : 'android_user_leave';
    // Asking for camera/microphone starts a system activity, which Android reports as the
    // user leaving. Only a screen lock is taken at face value while a prompt is in play.
    if (this.isMediaPermissionPromptRecent() && reason !== 'screen_lock') {
      return;
    }

    this.endCallFromLifecycle(reason);
  }

  private endCallFromLifecycle = (reason:string) => {
    console.log(`Ending active call because of native lifecycle event: ${reason}`);

    const currentCall:ICall|undefined = this.getCurrentCall() || this.state.preparedCall;

    if (currentCall) {
      this.hangupCall(currentCall);
      return;
    }

    if (this.state.settingUpCall > 0) {
      const callId = this.state.settingUpCall;
      this.setState({settingUpCall:0, dismissedCallId:callId, dismissedCallAt:Date.now()});
      this.props.respond({participant:this.props.userId, responseStatus:'DENIED', callId});
      return;
    }

    this.disConnect();
  }

  private resumeWakeLockHandler = () => {
    this.refreshCallWakeLocks(true);
  }

  private visibilityWakeLockHandler = () => {
    this.refreshCallWakeLocks();
  }

  private webWakeLockReleaseHandler = () => {
    this.webWakeLock = undefined;
    if (this.shouldKeepScreenAwake()) {
      this.requestWebWakeLock();
    }
  }

  private shouldKeepScreenAwake = () => {
    return this.componentMounted
      && (this.state.preparedCall !== undefined || this.state.isInCall || this.state.settingUpCall > 0);
  }

  private getInsomniaPlugin = () => {
    const win = (window as any);
    return win.plugins && win.plugins.insomnia ? win.plugins.insomnia : undefined;
  }

  private isAndroidCordova = () => {
    const cordova = (globalThis as any).cordova;
    return !!(cordova && cordova.platformId === 'android');
  }

  private refreshCallWakeLocks = (forceNativeRefresh:boolean = false) => {
    if (!this.shouldKeepScreenAwake()) {
      this.releaseCallWakeLocks();
      return;
    }

    this.requestNativeWakeLock(forceNativeRefresh);
    this.requestWebWakeLock();
  }

  private requestNativeWakeLock = (force:boolean = false) => {
    const insomnia = this.getInsomniaPlugin();
    if (!insomnia || typeof insomnia.keepAwake !== 'function') {
      return;
    }

    if (!force && (this.nativeWakeLockActive || this.nativeWakeLockRequestInFlight)) {
      return;
    }

    this.nativeWakeLockRequestInFlight = true;
    try {
      insomnia.keepAwake(
        () => {
          const shouldKeepAwake = this.shouldKeepScreenAwake();
          this.nativeWakeLockRequestInFlight = false;
          this.nativeWakeLockActive = true;
          if (!shouldKeepAwake) {
            this.releaseNativeWakeLock();
          }
        },
        (error:any) => {
          this.nativeWakeLockRequestInFlight = false;
          this.nativeWakeLockActive = false;
          console.warn('Insomnia keepAwake failed:', error);
        }
      );
    } catch (error) {
      this.nativeWakeLockRequestInFlight = false;
      this.nativeWakeLockActive = false;
      console.warn('Insomnia keepAwake threw:', error);
    }
  }

  private requestWebWakeLock = () => {
    const nav = (navigator as any);
    if (this.webWakeLock || !nav.wakeLock || typeof nav.wakeLock.request !== 'function') {
      return;
    }

    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }

    nav.wakeLock.request('screen')
      .then((lock:any) => {
        this.webWakeLock = lock;
        if (lock && typeof lock.addEventListener === 'function') {
          lock.addEventListener('release', this.webWakeLockReleaseHandler);
        }
        if (!this.shouldKeepScreenAwake()) {
          this.releaseWebWakeLock();
        }
      })
      .catch((error:any) => {
        console.warn('Web wake lock request failed:', error);
      });
  }

  private releaseCallWakeLocks = () => {
    this.releaseWebWakeLock();
    this.releaseNativeWakeLock();
  }

  private releaseWebWakeLock = () => {
    const lock = this.webWakeLock;
    this.webWakeLock = undefined;

    if (!lock) {
      return;
    }

    try {
      if (typeof lock.removeEventListener === 'function') {
        lock.removeEventListener('release', this.webWakeLockReleaseHandler);
      }
      if (typeof lock.release === 'function') {
        lock.release().catch((error:any) => {
          console.warn('Web wake lock release failed:', error);
        });
      }
    } catch (error) {
      console.warn('Web wake lock release threw:', error);
    }
  }

  private releaseNativeWakeLock = () => {
    const shouldRelease = this.nativeWakeLockActive || this.nativeWakeLockRequestInFlight;
    this.nativeWakeLockActive = false;
    this.nativeWakeLockRequestInFlight = false;

    if (!shouldRelease) {
      return;
    }

    const insomnia = this.getInsomniaPlugin();
    if (!insomnia || typeof insomnia.allowSleepAgain !== 'function') {
      return;
    }

    try {
      insomnia.allowSleepAgain(
        undefined,
        (error:any) => {
          console.warn('Insomnia allowSleepAgain failed:', error);
        }
      );
    } catch (error) {
      console.warn('Insomnia allowSleepAgain threw:', error);
    }
  }

  private muteVideo = () => {
    if (this.state.video && this.localStream) {
      this.localStream.getVideoTracks().forEach(track => track.enabled = false);
    } else if (this.localStream) {
      this.localStream.getVideoTracks().forEach(track => track.enabled = true);
    }
    this.setState({video:!this.state.video});
  }

  /**
   * Whether this device is the one handling the call, rather than another device the same
   * account is signed in on. The participant row is per account, so without this both a
   * phone and a computer would follow it into the meeting room as the same video user.
   */
  private isHandlingHere = (call:ICall):boolean => {
    if (call.id !== undefined && this.engagedCallId === call.id) {
      return true; // answered, or joined as the caller, on this device
    }
    return this.placedCallHere(call);
  }

  private placedCallHere = (call:ICall):boolean => {
    const {callRequestChatId, callRequestTime, userId} = this.props;
    return wasCallPlacedOnThisDevice(call, userId, callRequestChatId, callRequestTime);
  }

  /**
   * The account has answered, but not on this device and not in this app session. Whoever
   * took the call is in the meeting room; this device must stay out of it.
   */
  private isTakenElsewhere = (call:ICall, myStatus:ICallParticipant):boolean => {
    return myStatus.responseStatus === 'ANSWER'
      && !this.state.isInCall
      && !this.isHandlingHere(call);
  }

  /**
   * Releases everything held for a call that is being taken somewhere else. Deliberately
   * silent: sending a hang-up here would end the call on the device that answered it.
   */
  private standDownFromCall = () => {
    if (this.state.preparedCall === undefined && this.state.settingUpCall === 0) {
      return; // nothing held here
    }

    console.log("Standing down: this call is being taken on another device.");

    this.invalidateCallSetup();
    this.clearConnectyCubeListeners();
    // The video session is never left and the account session is never destroyed here.
    // This device never entered the room, and both calls could reach across to the session
    // the other device is using - the whole point of standing down is to leave it alone.
    this.setState({
      preparedCall:undefined,
      videoSession:undefined,
      callSetupState:'IDLE',
      isInCall:false,
      clickedAnswer:0,
      remoteStreams:[],
      settingUpCall:0,
    });
    this.stopAndReleaseLocalStream();
    this.detachMediaElementsForTeardown();
  }

  private getCurrentCall = ():ICall|undefined => {
    const preparedId = this.state.preparedCall?.id;
    return preparedId === undefined ? undefined : this.props.calls.find((c) => c.id === preparedId);
  }

  private isDismissed = (callId:number):boolean => {
    return callId === this.state.dismissedCallId
      && Date.now() - this.state.dismissedCallAt < DISMISSED_CALL_GUARD_MS;
  }

  /**
   * Single place that decides what this device should be doing about the calls it knows
   * about. The backend is authoritative about whether a call is still going on, so this
   * only picks the call to show and moves the local media setup along.
   */
  private checkForCalls = () => {
    const {calls, callData, userId, isOnline} = this.props;
    const knownCalls:ICall[] = Array.isArray(calls) ? calls : [];

    if (!isOnline || !this.state.init) {
      // Reconnecting, or still waiting for data. Nothing can be set up yet; just keep the
      // placeholder pointing at the real call once it arrives.
      if (this.state.settingUpCall === 0) {
        return;
      }
      const pending = this.selectCurrentCall(knownCalls);
      if (pending && pending.id !== undefined && this.state.settingUpCall !== pending.id
        && isCallJoinable(pending, userId)) {
        this.setState({settingUpCall:pending.id});
      }
      return;
    }

    if (!callData || !callData.connectycubeId || !callData.connectycubePassword) {
      return;
    }

    const currentCall:ICall|undefined = this.selectCurrentCall(knownCalls);

    if (!currentCall) {
      // Either everyone left or the backend ended the call. Tear down whatever is running.
      if (this.state.preparedCall !== undefined) {
        this.hangupCall(this.state.preparedCall);
      } else if (this.state.isInCall) {
        this.disConnect();
      } else {
        this.stopAndReleaseLocalStream();
      }
      return;
    }

    const myStatus:ICallParticipant|undefined = getParticipant(currentCall, userId);
    if (!myStatus || hasExited(myStatus)) {
      return;
    }

    if (this.isTakenElsewhere(currentCall, myStatus)) {
      this.standDownFromCall();
      return;
    }

    const connectedOthers = countConnectedOthers(currentCall, userId);

    if (this.state.preparedCall === undefined) {
      // An incoming call rings on every device the account is signed in on. An outgoing one
      // belongs to the device it was placed from - the others must not open the camera for
      // a call their user is not making.
      const shouldPrepare = myStatus.responseStatus === 'AWAITING'
        && (currentCall.initiator !== userId || this.isHandlingHere(currentCall));
      if (shouldPrepare) {
        void this.prepareCall(currentCall);
      }
      return;
    }

    // The caller joins as soon as somebody picks up. Everybody else joins by answering,
    // which is what marks this device as the one handling the call.
    const shouldJoin = connectedOthers > 0
      && myStatus.responseStatus === 'AWAITING'
      && this.isHandlingHere(currentCall);
    if (shouldJoin) {
      void this.joinCall(currentCall);
    }
  }

  /**
   * The call this device should be showing: still going on, still has somebody to talk to,
   * not one the user just dismissed. Ties are broken by the newest id, but the backend now
   * folds simultaneous requests into one call so competing calls should not occur.
   */
  private selectCurrentCall = (calls:ICall[]):ICall|undefined => {
    let selected:ICall|undefined;

    for (const call of calls) {
      if (call.id === undefined || this.isDismissed(call.id)) {
        continue;
      }
      if (countPresent(call) < 2 || !isPresent(getParticipant(call, this.props.userId))) {
        continue;
      }
      // Only a call that is still ringing, or the one this device is already in. There is no
      // way back into a call that has moved on: an old row must never put the call screen up.
      if (!isCallEligibleForDisplay(call, this.state.preparedCall?.id, this.state.isInCall)) {
        continue;
      }
      if (selected === undefined || (selected.id !== undefined && call.id > selected.id)) {
        selected = call;
      }
    }

    return selected;
  }

  private prepareCall = async (call:ICall) => {
    if (Date.now() - 1000 < this.prepCallTime) {return;}
    this.prepCallTime = Date.now();

    if (this.activePreparedCallId === call.id && (this.state.videoSession !== undefined || this.localMediaPromise !== undefined)) {
      return;
    }

    const setupToken = this.beginCallSetup(call.id!);

    // This device owns the call it placed from here on. The outgoing call request is only
    // recognisable for a minute, and a permission dialog can easily outlast that - without
    // this the caller would stop recognising its own call and never join it.
    if (this.placedCallHere(call)) {
      this.engagedCallId = call.id;
    }

    this.setState({preparedCall:call, remoteStreams:[], clickedAnswer:0, callSetupState:'PREPARING'});

    // Permission is settled before any ConnectyCube session exists. The OS dialog puts the
    // app in the background, where the SDK's keepalives stop and its signalling socket
    // refuses to reconnect while the document is hidden - a session created before the
    // dialog is dead by the time the other side answers, and joining it fails.
    const permitted = await this.ensureLocalMediaPermission(call, setupToken);
    if (!permitted || !this.isCurrentCallSetup(call.id!, setupToken)) {
      return;
    }

    this.startConnectyCubeSession(call, setupToken);
  }

  /**
   * Gets camera and microphone access, and reports the call as failed if it is refused.
   * Returns whether call setup may continue.
   */
  private ensureLocalMediaPermission = async (call:ICall, setupToken:number): Promise<boolean> => {
    try {
      const {granted, prompted} = await this.requestMediaPermission();

      if (!this.isCurrentCallSetup(call.id!, setupToken)) {
        return false;
      }

      if (!granted) {
        this.failCallLocally("Camera/Mic access not granted.", call);
        this.showErrorScreenSafely();
        return false;
      }

      if (prompted) {
        // Coming back from the dialog: let the newly granted permission settle and wait for
        // the app to be on screen again before opening the camera or a socket.
        await this.wait(MEDIA_PERMISSION_PROBE_COOLDOWN_MS);
        await this.waitForVisibleDocument(call, setupToken);

        if (!this.isCurrentCallSetup(call.id!, setupToken)) {
          return false;
        }
      }

      return true;
    } catch (error) {
      if (this.isCurrentCallSetup(call.id!, setupToken)) {
        const message = "Permission-error accessing camera " + this.describeError(error);
        if (this.isMediaPermissionDenied(error)) {
          this.failCallLocally(message, call);
        } else {
          this.reportErrorCall(message, call);
        }
        this.showErrorScreenSafely();
      }
      return false;
    }
  }

  private requestMediaPermission = async (): Promise<ICallPermissionResult> => {
    this.mediaPermissionRequestInProgress = true;
    try {
      const result = await ensureCallPermission();
      if (result.prompted) {
        this.mediaPermissionPromptEndedAt = Date.now();
      }
      return result;
    } finally {
      this.mediaPermissionRequestInProgress = false;
    }
  }

  private startConnectyCubeSession = (call:ICall, setupToken:number) => {
    const ConnectyCube = (window as any).ConnectyCube;

    if (ConnectyCube !== undefined && call && this.props.callData.connectycubeId) {
      try {
        ConnectyCube.init(CREDENTIALS);
        console.log("ConnectyCube SDK version:", ConnectyCube.version || ConnectyCube.sdkVersion || "unknown");
        const userCredentials = { login: "vv"+this.props.userId, password: this.props.callData.connectycubePassword };

        ConnectyCube.videochatconference.onRemoteStreamListener = (session:any, userID:any, remoteStream:any) => {
          if (!this.isCurrentCallSetup(call.id!, setupToken)) {
            return;
          }

          try {
            const streamId = ""+userID;
            // Keep the stream keyed by user so a re-publish (which bad remote
            // connectivity triggers repeatedly) just updates the existing slot.
            this.remoteStreamsByUser.set(streamId, remoteStream);

            this.setState((prev:IState) => {
              if (prev.remoteStreams.includes(streamId)) {
                return null;
              }
              return { remoteStreams: [...prev.remoteStreams, streamId] };
            }, this.syncRemoteVideoElements);
          } catch (error) {
            console.error("onRemoteStreamListener failed:", error);
          }
        };

        ConnectyCube.videochatconference.onParticipantLeftListener = (session:any, userID:any) => {
          if (!this.isCurrentCallSetup(call.id!, setupToken)) {
            return;
          }

          try {
            this.removeRemoteParticipant(""+userID);
          } catch (error) {
            console.error("onParticipantLeftListener failed:", error);
          }
        };
        ConnectyCube.videochatconference.onSlowLinkListener = (session:any, userId:any, uplink:any, nacks:any) => {
          console.log("Slow link detected. Uplink: "+uplink+" NACKs: "+nacks+" User:"+userId);
        };
        ConnectyCube.videochatconference.onRemoteConnectionStateChangedListener = (session:any, userId:any, iceState:any) => {
          console.log("Remote connection state changed. UserId: "+userId+" ICEState: "+iceState);

          if (!this.isCurrentCallSetup(call.id!, setupToken)) {
            return;
          }

          const normalizedState = (""+iceState).toLowerCase();
          if (normalizedState === 'failed' || normalizedState === 'closed') {
            // The remote peer's connection died (e.g. their connectivity dropped).
            // Remove just that participant's tile and let the call continue for
            // everyone else instead of thrashing the dead peer's media.
            try {
              this.removeRemoteParticipant(""+userId);
            } catch (error) {
              console.error("Failed to drop remote participant after ICE "+normalizedState+":", error);
            }
          }
        };
        ConnectyCube.videochatconference.onSessionConnectionStateChangedListener = (session:any, iceState:any) => {
          console.log("Session connection state changed. ICEState: "+iceState);
        };
        ConnectyCube.videochatconference.onErrorListener = (session:any, error:any) => {
          if (!this.isCurrentCallSetup(call.id!, setupToken)) {
            return;
          }

          // ConnectyCube reports auxiliary data-channel negotiation errors here
          // even when the audio/video connection succeeds. Treating every one
          // as fatal sends TECH_ERROR to the backend and tears down an otherwise
          // connected call. Actual setup failures are handled by createSession,
          // getUserMedia, and join below; ICE failures are handled separately.
          console.warn("Non-fatal conference error: " + this.describeError(error));
        };


        ConnectyCube.createSession(userCredentials)
          .then((session:any) => {
            if (!this.isCurrentCallSetup(call.id!, setupToken)) {
              void this.destroyConnectyCubeSession(session);
              return;
            }

            const videoSession = ConnectyCube.videochatconference.createNewSession();
            this.setState({session, videoSession}, () => {
              this.localMediaPromise = this.attachLocalStream(videoSession, call, setupToken);
            });
          })
          .catch((error:any) => {
            if (!this.isCurrentCallSetup(call.id!, setupToken)) {
              return;
            }
            this.reportErrorCall("Call Error: session error"+ error, call);
          });
      } catch (e) {
        this.reportErrorCall("Call Error: "+e, call);
      }
    } else {
      // TODO handle this.
      console.log("awaiting connectycube install prep");
      return;
    }
  }

  private attachLocalStream = async (videoSession:any, call:ICall, setupToken:number): Promise<boolean> => {
    if (!this.isCurrentCallSetup(call.id!, setupToken)) {
      return false;
    }

    try {
      // Permission is already settled by prepareCall, before this session existed.
      return await this.connectLocalVideo(videoSession, call, setupToken);
    } catch (error) {
      if (this.isCurrentCallSetup(call.id!, setupToken)) {
        const message = "Permission-error accessing camera " + this.describeError(error);
        if (this.isMediaPermissionDenied(error)) {
          this.failCallLocally(message, call);
        } else {
          this.reportErrorCall(message, call);
        }
        this.showErrorScreenSafely();
      }
      return false;
    }
  }

  private wait = (ms:number): Promise<void> => {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  private truncateErrorText = (value:string, maxLength:number = 500): string => {
    const normalizedValue = value.replace(/\s+/g, ' ').trim();
    if (normalizedValue.length <= maxLength) {
      return normalizedValue;
    }

    return normalizedValue.substring(0, maxLength - 3) + '...';
  }

  private formatErrorValue = (value:any): string => {
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }

    if (value instanceof Error) {
      const errorParts = [value.name, value.message].filter(Boolean);
      if (errorParts.length > 0) {
        return errorParts.join(': ');
      }
    }

    try {
      return JSON.stringify(value);
    } catch (_) {
      try {
        return String(value);
      } catch (__){
        return '[unserializable]';
      }
    }
  }

  private describeError = (error:any): string => {
    if (error === undefined || error === null) {
      return 'unknown error';
    }

    if (typeof error === 'string') {
      return this.truncateErrorText(error);
    }

    const parts:string[] = [];
    const addPart = (part:string) => {
      const normalizedPart = this.truncateErrorText(part, 240);
      if (normalizedPart && parts.indexOf(normalizedPart) < 0) {
        parts.push(normalizedPart);
      }
    };

    if (error instanceof Error) {
      const errorHeadline = [error.name, error.message].filter(Boolean).join(': ');
      if (errorHeadline) {
        addPart(errorHeadline);
      }
    }

    if (typeof error === 'object') {
      const keysToCheck = ['name', 'message', 'code', 'status', 'statusText', 'type', 'reason'];
      keysToCheck.forEach((key) => {
        const value = error[key];
        if (value !== undefined && value !== null && value !== '') {
          addPart(key + '=' + this.formatErrorValue(value));
        }
      });

      if (parts.length === 0) {
        try {
          const objectKeys = Object.keys(error);
          if (objectKeys.length > 0) {
            addPart('keys=' + objectKeys.slice(0, 10).join(','));
          }
        } catch (_) {
          // ignore
        }
      }
    }

    if (parts.length === 0) {
      try {
        addPart(String(error));
      } catch (_) {
        addPart('unserializable error');
      }
    }

    return this.truncateErrorText(parts.join('; '));
  }

  private describeMediaStream = (stream?:MediaStream): string => {
    if (!stream) {
      return 'stream=missing';
    }

    try {
      const tracks = stream.getTracks();
      const audioTracks = tracks.filter((track) => track.kind === 'audio').length;
      const videoTracks = tracks.filter((track) => track.kind === 'video').length;
      return 'streamTracks=' + tracks.length + ',audioTracks=' + audioTracks + ',videoTracks=' + videoTracks;
    } catch (_) {
      return 'stream=present';
    }
  }

  private describeVideoElement = (videoElement?:HTMLVideoElement | null): string => {
    if (!videoElement) {
      return 'videoElement=missing';
    }

    return 'videoElement=present,readyState=' + videoElement.readyState + ',networkState=' + videoElement.networkState + ',muted=' + videoElement.muted + ',autoplay=' + videoElement.autoplay;
  }

  private buildVideoLoadErrorMessage = (stage:string, error:any, videoElement?:HTMLVideoElement | null, stream?:MediaStream): string => {
    const hasNavigator = typeof navigator !== 'undefined';
    const mediaDevicesAvailable = hasNavigator && !!navigator.mediaDevices;
    const getUserMediaAvailable = mediaDevicesAvailable && typeof navigator.mediaDevices.getUserMedia === 'function';

    return this.truncateErrorText(
      'Failed to load video'
      + ' | stage=' + stage
      + ' | reason=' + this.describeError(error)
      + ' | mediaDevices=' + (mediaDevicesAvailable ? 'available' : 'missing')
      + ' | getUserMedia=' + (getUserMediaAvailable ? 'available' : 'missing')
      + ' | ' + this.describeVideoElement(videoElement)
      + ' | ' + this.describeMediaStream(stream),
      700
    );
  }

  private showErrorScreenSafely = () => {
    try {
      this.setState({showErrorScreen:true});
    } catch (error) {
      try {
        console.error('Failed to show call error screen:', error);
      } catch (_) {
        // ignore
      }
    }
  }

  private reportVideoLoadFailure = (stage:string, error:any, call:ICall, videoElement?:HTMLVideoElement | null, stream?:MediaStream) => {
    let message = 'Failed to load video';

    try {
      message = this.buildVideoLoadErrorMessage(stage, error, videoElement, stream);
    } catch (formatError) {
      try {
        message = this.truncateErrorText(
          'Failed to load video'
          + ' | stage=' + stage
          + ' | reason=' + this.describeError(error)
          + ' | formatterFailure=' + this.describeError(formatError)
        );
      } catch (_) {
        message = 'Failed to load video | stage=' + stage + ' | unable to format detailed error';
      }
    }

    try {
      if (this.isExpectedGetUserMediaDenial(stage, error)) {
        this.failCallLocally(message, call);
      } else {
        this.reportErrorCall(message, call);
      }
    } catch (reportError) {
      try {
        console.error('Failed to report video load failure:', reportError);
      } catch (_) {
        // ignore
      }
    }

    this.showErrorScreenSafely();
  }

  private stopStreamTracksSafely = (stream?:MediaStream) => {
    if (!stream) {
      return;
    }

    try {
      stream.getTracks().forEach((track) => track.stop());
    } catch (_) {
      // ignore
    }
  }

  private stopMediaElementStreamsSafely = () => {
    try {
      document.querySelectorAll('video,audio').forEach((el: HTMLMediaElement) => {
        const obj = (el as any).srcObject;
        const hasTracks = obj && typeof obj.getTracks === 'function';

        if (hasTracks) {
          obj.getTracks().forEach((track:MediaStreamTrack) => {track.stop();});
          (el as any).srcObject = null;
        }
      });
    } catch (_) {
      // ignore
    }
  }

  private configureVideoPlaybackAttributes = (videoElement:HTMLVideoElement) => {
    videoElement.autoplay = true;

    try {
      videoElement.setAttribute('playsinline', 'true');
    } catch (_) {
      // ignore
    }

    try {
      videoElement.setAttribute('webkit-playsinline', 'true');
    } catch (_) {
      // ignore
    }
  }

  private cleanupFailedLocalVideo = (stream?:MediaStream) => {
    const videoElement = this.myVideoRef.current;
    if (videoElement) {
      try {
        if ((videoElement as any).srcObject === stream) {
          (videoElement as any).srcObject = null;
        }
        videoElement.pause();
      } catch (_) {
        // ignore
      }
    }

    this.stopStreamTracksSafely(stream);

    if (this.localStream === stream) {
      this.localStream = undefined;
    }
  }

  private playVideoElementWithRetry = async (videoElement:HTMLVideoElement): Promise<void> => {
    let firstError:any;

    this.configureVideoPlaybackAttributes(videoElement);

    try {
      await videoElement.play();
      return;
    } catch (error) {
      firstError = error;
      try {
        console.warn('Video play failed:', error);
      } catch (_) {
        // ignore
      }
    }

    await this.wait(100);

    try {
      await videoElement.play();
    } catch (retryError) {
      throw new Error(
        'play() failed after retry'
        + ' | first=' + this.describeError(firstError)
        + ' | retry=' + this.describeError(retryError)
      );
    }
  }

  private getMediaErrorName = (error:any): string => {
    return error && typeof error === 'object' && typeof error.name === 'string'
      ? error.name
      : '';
  }

  private getMediaErrorMessage = (error:any): string => {
    if (error && typeof error === 'object' && typeof error.message === 'string') {
      return error.message;
    }

    try {
      return String(error);
    } catch (_) {
      return '';
    }
  }

  private isMediaPermissionDenied = (error:any): boolean => {
    const name = this.getMediaErrorName(error);
    const message = this.getMediaErrorMessage(error).toLowerCase();

    return name === 'NotAllowedError'
      || name === 'PermissionDeniedError'
      || name === 'SecurityError'
      || message.indexOf('permission denied') >= 0
      || message.indexOf('denied permission') >= 0
      || message.indexOf('not allowed by the user agent') >= 0
      || message.indexOf('not allowed in the current context') >= 0
      || message.indexOf('requires a secure context') >= 0;
  }

  private isExpectedGetUserMediaDenial = (stage:string, error:any): boolean => {
    return stage.indexOf('getUserMedia') === 0 && this.isMediaPermissionDenied(error);
  }

  private isRecoverableGetUserMediaError = (error:any): boolean => {
    const name = this.getMediaErrorName(error);
    const message = this.getMediaErrorMessage(error).toLowerCase();

    return name === 'NotReadableError'
      || name === 'AbortError'
      || message.indexOf('could not start video source') >= 0
      || message.indexOf('camera is already in use') >= 0
      || message.indexOf('device in use') >= 0;
  }

  private waitForVisibleDocument = async (call:ICall, setupToken:number): Promise<boolean> => {
    if (typeof document === 'undefined' || !document.hidden) {
      return true;
    }

    const startedAt = Date.now();
    while (this.isCurrentCallSetup(call.id!, setupToken)
      && document.hidden
      && Date.now() - startedAt < LOCAL_MEDIA_VISIBILITY_WAIT_MS) {
      await this.wait(100);
    }

    return this.isCurrentCallSetup(call.id!, setupToken)
      && (typeof document === 'undefined' || !document.hidden);
  }

  private getLocalMediaWithRecovery = async (videoSession:any, call:ICall, setupToken:number): Promise<MediaStream | undefined> => {
    const mediaParams:MediaStreamConstraints = {
      audio: true,
      video: true
    };
    let lastError:any;

    for (let attempt = 0; attempt <= LOCAL_MEDIA_RETRY_DELAYS_MS.length; attempt++) {
      if (!this.isCurrentCallSetup(call.id!, setupToken)) {
        return undefined;
      }

      if (attempt > 0) {
        this.stopMediaElementStreamsSafely();
        await this.wait(LOCAL_MEDIA_RETRY_DELAYS_MS[attempt - 1]);
      }

      const documentVisible = await this.waitForVisibleDocument(call, setupToken);
      if (!this.isCurrentCallSetup(call.id!, setupToken)) {
        return undefined;
      }

      if (!documentVisible) {
        try {
          console.warn('Starting local media while document is still hidden.');
        } catch (_) {
          // ignore
        }
      }

      try {
        return await videoSession.getUserMedia(mediaParams);
      } catch (error) {
        lastError = error;

        if (!this.isRecoverableGetUserMediaError(error) || attempt === LOCAL_MEDIA_RETRY_DELAYS_MS.length) {
          break;
        }

        try {
          console.warn('Local video getUserMedia failed; retrying:', error);
        } catch (_) {
          // ignore
        }
      }
    }

    if (!this.isCurrentCallSetup(call.id!, setupToken)) {
      return undefined;
    }

    if (this.isRecoverableGetUserMediaError(lastError)) {
      try {
        const audioOnlyParams:MediaStreamConstraints = {
          audio: true,
          video: false
        };
        const audioOnlyStream = await videoSession.getUserMedia(audioOnlyParams);
        try {
          /*
          this.props.reportProblem(
            this.buildVideoLoadErrorMessage('getUserMediaVideoFallback', lastError, this.myVideoRef.current, audioOnlyStream)
          );
          */
        } catch (_) {
          // The fallback is still usable even if diagnostics fail.
        }
        return audioOnlyStream;
      } catch (audioOnlyError) {
        if (this.isCurrentCallSetup(call.id!, setupToken)) {
          this.reportVideoLoadFailure('getUserMediaAudioFallback', audioOnlyError, call, this.myVideoRef.current);
        }
        return undefined;
      }
    }

    this.reportVideoLoadFailure('getUserMedia', lastError, call, this.myVideoRef.current);
    return undefined;
  }

  private connectLocalVideo = async (videoSession:any, call:ICall, setupToken:number): Promise<boolean> => {
    const localStream = await this.getLocalMediaWithRecovery(videoSession, call, setupToken);
    if (!localStream) {
      return false;
    }

    if (!this.isCurrentCallSetup(call.id!, setupToken)) {
      this.stopStreamTracksSafely(localStream);
      return false;
    }

    this.localStream = localStream;

    const videoElement = this.myVideoRef.current;
    if (!videoElement) {
      this.cleanupFailedLocalVideo(localStream);
      this.reportVideoLoadFailure('attach', 'Local video element was not available', call, videoElement, localStream);
      return false;
    }

    try {
      // Clear any existing stream first
      if (videoElement.srcObject) {
        const oldStream = videoElement.srcObject as MediaStream;
        this.stopStreamTracksSafely(oldStream);
        videoElement.srcObject = null;
      }

      // Set the new stream
      videoElement.srcObject = localStream;
      videoElement.muted = true;
      const hasLocalVideo = localStream.getVideoTracks().length > 0;

      // Apply mirror effect for front camera
      // videoElement.style.transform = 'scaleX(-1)';
      // videoElement.style.webkitTransform = 'scaleX(-1)';

      // Force play on iOS - sometimes needed
      await this.playVideoElementWithRetry(videoElement);
      this.localMediaReady = true;
      this.setState({callSetupState:'READY', video:hasLocalVideo});
      return true;
    } catch (error) {
      this.cleanupFailedLocalVideo(localStream);
      this.reportVideoLoadFailure('playback', error, call, videoElement, localStream);
      return false;
    }
  }

  private syncRemoteVideoElements = () => {
    const order = this.state.remoteStreams;

    for (let i = 0; i < 4; i++) {
      const videoElement = document.getElementById('videochat' + i) as HTMLVideoElement | null;
      if (!videoElement) {
        continue;
      }

      const streamId = i < order.length ? order[i] : undefined;
      const desiredStream = streamId ? this.remoteStreamsByUser.get(streamId) : undefined;

      if (desiredStream) {
        if (videoElement.srcObject !== desiredStream) {
          this.detachRemoteVideoElement(videoElement);
          videoElement.srcObject = desiredStream;
          this.configureVideoPlaybackAttributes(videoElement);
          videoElement.muted = false;
          void this.playVideoElementWithRetry(videoElement).catch((err) => {
            console.error("Remote video play failed:", err);
          });
        }
      } else if (videoElement.srcObject) {
        this.detachRemoteVideoElement(videoElement);
      }
    }
  }

  private detachRemoteVideoElement = (videoElement:HTMLVideoElement) => {
    // Remote MediaStreamTracks are owned by the peer connection / ConnectyCube.
    // Never call track.stop() on them: stopping remote tracks while the peer is
    // renegotiating (exactly what bad remote connectivity triggers) can crash the
    // native WebRTC stack. Detaching is enough; the SDK releases the tracks.
    try {
      videoElement.srcObject = null;
    } catch (_) {
      // ignore
    }
  }

  private removeRemoteParticipant = (streamId:string) => {
    this.remoteStreamsByUser.delete(streamId);

    this.setState((prev:IState) => {
      if (!prev.remoteStreams.includes(streamId)) {
        return null;
      }
      return { remoteStreams: prev.remoteStreams.filter((id) => id !== streamId) };
    }, this.syncRemoteVideoElements);
  }

  private detachMediaElementsForTeardown = () => {
    this.remoteStreamsByUser.clear();

    try {
      document.querySelectorAll('video,audio').forEach((el: HTMLMediaElement) => {
        const obj = (el as any).srcObject;
        if (!(obj instanceof MediaStream)) {
          return;
        }

        // Only stop tracks we own (the local capture stream). Remote tracks must
        // be detached, not stopped (see detachRemoteVideoElement).
        if (obj === this.localStream) {
          obj.getTracks().forEach((t) => t.stop());
        }

        (el as any).srcObject = null;
      });
    } catch (_) {
      // ignore
    }
  }

  private disConnect = () => {
    this.invalidateCallSetup();
    this.releaseConnectyCubeResources();
    this.setState({preparedCall:undefined, session:undefined, videoSession:undefined, callSetupState:'IDLE', isInCall:false, clickedAnswer:0, remoteStreams:[]});

    this.stopAndReleaseLocalStream();

    this.detachMediaElementsForTeardown();
  }


  private joinCall = async (call:ICall) => {
    if (Date.now() - 1000 < this.joinCallTime) {return;}
    this.joinCallTime = Date.now();

    // From here on this device owns the call for this account: another device of the same
    // account must not follow the shared participant row into the meeting room.
    this.engagedCallId = call.id;

    this.setState({clickedAnswer:Date.now()})
    const ConnectyCube = (window as any).ConnectyCube;
    if (ConnectyCube !== undefined && call && call.externalId && this.props.callData.connectycubeId && this.state.videoSession !== undefined && this.state.isInCall === false) {
      if (this.activePreparedCallId !== call.id) {
        return;
      }

      if (!this.localMediaReady) {
        if (!this.localMediaPromise) {
          this.props.displayErrorMessage("Call setup is still initializing, please try again.");
          return;
        }

        const mediaReady = await this.localMediaPromise;
        if (!mediaReady || !this.localMediaReady || this.activePreparedCallId !== call.id) {
          return;
        }
      }

      this.setState({isInCall:true});

      let name:string = "unknwon";
      for (const mem of this.props.family) {
        if (mem.id === this.props.userId) {
          name = mem.name;
        }
      }

      this.state.videoSession
        .join(call.externalId, this.props.callData.connectycubeId, name)
        .then(() => {
          // const roomId = this.state.videoSession.currentRoomId;
          if (this.activePreparedCallId !== call.id) {
            void this.releaseConnectyCubeResources();
            return;
          }
          this.props.respond({participant:this.props.userId, responseStatus:'ANSWER', callId:call.id});
        })
        .catch((error:any) => {
          if (this.activePreparedCallId !== call.id) {
            // Already torn down. Reporting now would tell the backend this account failed
            // a call it is no longer in, ending it on whatever device did take it.
            return;
          }
          this.reportErrorCall("Call Error: join room error"+ error, call);
        });
        
    } else {
      this.props.displayErrorMessage("Call setup error, please try again later");
      return;
    }
  }

  private hangupCall = (call:ICall) => {
    if (Date.now() - 1000 < this.closeCallTime) {return;}
    this.closeCallTime = Date.now();

    this.props.displayErrorMessage("Call ended");
    const myStatus = call.participants.find(p => p.participant === this.props.userId);
    if (myStatus && myStatus.cleanExitTime && myStatus.cleanExitTime > 0) {
      // already hung up
    } else if (myStatus && myStatus.responseStatus === 'AWAITING') {
      this.props.respond({participant:this.props.userId, responseStatus:'DENIED', callId:call.id});
    } else {
      this.props.respond({participant:this.props.userId, responseStatus:'HANG_UP',  callId:call.id});
    }
    this.invalidateCallSetup();
    this.releaseConnectyCubeResources();
    this.setState({preparedCall:undefined, session:undefined, videoSession:undefined, callSetupState:'IDLE', isInCall:false, dismissedCallId:call.id, dismissedCallAt:Date.now(), remoteStreams:[]});

    this.stopAndReleaseLocalStream();

    this.detachMediaElementsForTeardown();
  }

  private stopAndReleaseLocalStream = () => {
    this.localMediaReady = false;
    this.localMediaPromise = undefined;
    const win = (window as any);
    if (this.localStream) {
      this.stopStreamTracksSafely(this.localStream);

      // Detach from element if any
      const videoEl = this.myVideoRef.current || document.getElementById('myvideo') as HTMLVideoElement | null;
      if (videoEl && (videoEl as any).srcObject === this.localStream) {
        (videoEl as any).srcObject = null;
      }

      this.localStream = undefined;
    }
  }

  private failCallLocally = (msg:string, call:ICall) => {
    try {
      console.log(msg);
    } catch (_) {
      // ignore
    }

    this.invalidateCallSetup();
    this.releaseConnectyCubeResources();
    this.detachMediaElementsForTeardown();

    try {
      this.setState({preparedCall:undefined, session:undefined, videoSession:undefined, callSetupState:'IDLE', isInCall:false, remoteStreams:[]});
    } catch (stateError) {
      try {
        console.error('Failed to update call error state:', stateError);
      } catch (_) {
        // ignore
      }
    }

    try {
      this.props.displayErrorMessage(msg);
    } catch (displayError) {
      try {
        console.error('Failed to display call error message:', displayError);
      } catch (_) {
        // ignore
      }
    }

    // Only the device the user is actually taking the call on may report a failure: the
    // backend would treat it as this account leaving, ending the call on the other device.
    if (call && call.id !== undefined && this.isHandlingHere(call)) {
      try {
        this.props.respond({participant:this.props.userId, responseStatus:'TECH_ERROR', callId:call.id});
      } catch (respondError) {
        try {
          console.error('Failed to send TECH_ERROR response:', respondError);
        } catch (_) {
          // ignore
        }
      }
    }
  }

  private reportErrorCall = (msg:string, call:ICall) => {
    this.failCallLocally(msg, call);

    try {
      // this.props.reportProblem(msg);
    } catch (reportError) {
      try {
        console.error('Failed to report call problem:', reportError);
      } catch (_) {
        // ignore
      }
    }
  }

  private generateVideoLayout = (numStreams:number, streamIndex:number):React.CSSProperties => {
    const styles:React.CSSProperties = {
      position: 'absolute',
      border: '0px solid #000',
      backgroundColor: '#000',
      transition: 'all 0.5s ease',
    };
    
    if (numStreams === 0) {
      styles.top = '50%';
      styles.left = '50%';
      styles.width = '0%';
      styles.height = '0%';
    } else if (numStreams === 1) {
      if (streamIndex === 0) {
        styles.top = '0%';
        styles.left = '0%';
        styles.width = '100%';
        styles.height = '100%';
      } else { return this.generateVideoLayout(0, 0); }
    } else if (numStreams === 2) {
      if (streamIndex < 2) {
        styles.width = '100%';
        styles.height = '50%';
        styles.top = (streamIndex * 50) + '%';
        styles.left = '0%';
      } else { return this.generateVideoLayout(0, 0); }
    } else if (numStreams === 3) {
      if (streamIndex < 3) {
        styles.width = '50%';
        styles.height = '50%';
        if (streamIndex === 0) {
          styles.top = '0%';
          styles.left = '25%';
        } else if (streamIndex === 1) {
          styles.top = '50%';
          styles.left = '0%';
        } else if (streamIndex === 2) {
          styles.top = '50%';
          styles.left = '50%';
        }
      } else { return this.generateVideoLayout(0, 0); }
    } else if (numStreams === 4) {
      if (streamIndex < 4) {
        styles.width = '50%';
        styles.height = '50%';
        styles.top = (streamIndex < 2) ? '0%' : '50%';
        styles.left = (streamIndex % 2 === 0) ? '0%' : '50%';
      } else { return this.generateVideoLayout(0, 0); }
    } else {
      // For more than 4 streams, arrange in a grid
      const gridSize = Math.ceil(Math.sqrt(numStreams));
      styles.width = (100 / gridSize) + '%';
      styles.height = (100 / gridSize) + '%';
      styles.top = (Math.floor(streamIndex / gridSize) * (100 / gridSize)) + '%';
      styles.left = ((streamIndex % gridSize) * (100 / gridSize)) + '%';
    }

    return styles;
  }

  private beginCallSetup = (callId:number): number => {
    this.callSetupToken += 1;
    this.activePreparedCallId = callId;
    this.localMediaReady = false;
    this.localMediaPromise = undefined;
    this.remoteStreamsByUser.clear();
    return this.callSetupToken;
  }

  private invalidateCallSetup = () => {
    this.callSetupToken += 1;
    this.activePreparedCallId = undefined;
    this.localMediaReady = false;
    this.localMediaPromise = undefined;
  }

  private isCurrentCallSetup = (callId:number, setupToken:number): boolean => {
    return this.callSetupToken === setupToken && this.activePreparedCallId === callId;
  }

  private clearConnectyCubeListeners = () => {
    const ConnectyCube = (window as any).ConnectyCube;

    if (ConnectyCube?.videochatconference) {
      ConnectyCube.videochatconference.onRemoteStreamListener = undefined;
      ConnectyCube.videochatconference.onParticipantLeftListener = undefined;
      ConnectyCube.videochatconference.onSlowLinkListener = undefined;
      ConnectyCube.videochatconference.onRemoteConnectionStateChangedListener = undefined;
      ConnectyCube.videochatconference.onSessionConnectionStateChangedListener = undefined;
      ConnectyCube.videochatconference.onErrorListener = undefined;
    }
  }

  private releaseConnectyCubeResources = () => {
    const currentVideoSession = this.state.videoSession;
    const currentSession = this.state.session;

    this.clearConnectyCubeListeners();

    if (currentVideoSession && typeof currentVideoSession.leave === 'function') {
      try {
        currentVideoSession
          .leave()
          .then(() => {
            console.log("Left video room");
          })
          .catch((error:any) => {
            console.log("Leave video room error: " + error);
          });
      } catch (error) {
        console.log("Leave video room error:", error);
      }
    }

    if (currentSession) {
      void this.destroyConnectyCubeSession(currentSession);
    }
  }

  private destroyConnectyCubeSession = async (session:any) => {
    const ConnectyCube = (window as any).ConnectyCube;
    if (!ConnectyCube || typeof ConnectyCube.destroySession !== 'function') {
      return;
    }

    try {
      await ConnectyCube.destroySession();
    } catch (error) {
      console.log("Destroy ConnectyCube session error:", error);
    }
  }

}

CallDisplay.contextType = CordovaContext;

function mapStateToProps(state: any) {
  const allCalls:ICall[] = state.calls.calls?state.calls.calls:[];
  const callData: ICallData = state.calls.callData;
  const userId = state.user.session.data.id;
  
  // Wide enough to still cover a call that has been running for hours; the backend ends a
  // call long before this, so nothing here depends on the window to make a call go away.
  const recentCalls = allCalls.filter(c => c.start > Date.now() - MAX_CALL_LIFETIME_MS);

  return {
    userId,
    calls:recentCalls,
    callData,
    family: state.family.family,
    isOnline:state.connection.loadCompleted,
    callNotificationTime: state.calls.callNotificationTime,
    callNotificationCallId: state.calls.callNotificationCallId,
    callRequestChatId: state.calls.callRequestChatId ? state.calls.callRequestChatId : 0,
    callRequestTime: state.calls.callRequestTime ? state.calls.callRequestTime : 0,
  };
}

const mapDispatchToProps = {
  call:CallActions.newCall,
  respond:CallActions.callAction,
  reportProblem: UserActions.reportProblem,
  displayErrorMessage: UserActions.displayErrorMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ConnecycubeStyle)(CallDisplay));



// WEBPACK FOOTER //
// ./src/components/calls/CallDisplay.tsx